/**
 * In-place soft-delete filtering for AppSheet-synced collections.
 *
 * The sync no longer moves vanished rows to a `<collection>_archive` collection —
 * it stamps `_deleted: true` on the live doc after a 3-sync grace period (see
 * functions/src/dataHub/appsheet/reconcileGuards.ts, false-deletion plan D2).
 * Active views (pickers, calendars, related-record cards) must exclude those
 * docs; history/union readers that deliberately include archived rows should NOT.
 *
 * Always filter POST-FETCH with this helper. A Firestore
 * `where('_deleted', '!=', true)` query is wrong here: `!=` only matches docs
 * where the field EXISTS, which would silently exclude every doc that has never
 * entered the grace period (i.e. almost all of them).
 */

export interface SoftDeletable {
  _deleted?: unknown;
}

/** True when the sync has soft-deleted this record (hidden from active views). */
export function isSoftDeleted(record: SoftDeletable | null | undefined): boolean {
  return record?._deleted === true;
}

/** Drop soft-deleted records from a fetched list (active-view reads). */
export function excludeSoftDeleted<T extends SoftDeletable>(records: T[]): T[] {
  return records.filter((record) => !isSoftDeleted(record));
}

/** The sync's `_deletedAt` / a block's start as a "yyyy-mm-dd" day, or null when unreadable. */
function dayOf(value: unknown): string | null {
  if (typeof value === 'string') {
    const us = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return iso ? `${iso[1]}-${iso[2]}-${iso[3]}` : null;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  if (typeof value === 'object' && value !== null && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return dayOf((value as { toDate: () => Date }).toDate());
  }
  return null;
}

/**
 * Client twin of functions/src/projects/softDeletedBlock.ts `isRemovedBeforeItsDay` (parity pinned by
 * softDeleteParity.test.ts, which requires the REAL server file).
 *
 * A soft-deleted schedule block that was removed BEFORE its own day, so it can never have been worked.
 * Money-adjacent screens (invoice timeline, unsent-invoice review) drop only this class: a block flagged on
 * or after its own day is ambiguous (the visit may have happened and the row tidied later), so it keeps
 * showing, exactly as before. Visit/calendar screens use the strict `isSoftDeleted`.
 */
export function isRemovedBeforeItsDay(record: Record<string, unknown> | null | undefined): boolean {
  if (!isSoftDeleted(record as SoftDeletable)) return false;
  const d = record as Record<string, unknown>;
  // A person's CONFIRMED delete (reason below, pinned equal to the server literal by softDeleteParity.test.ts) is
  // removed whatever its day.
  if (d['_deletedReason'] === 'deleted_in_officehub') return true;
  const blockDay = dayOf(d['Start Date']) ?? dayOf(d['Start']);
  const removedDay = dayOf(d['_deletedAt']);
  return blockDay !== null && removedDay !== null && removedDay < blockDay;
}
